import { forwardRef, useCallback, useContext, useEffect, useRef } from 'react'
import { useComposedRefs } from '../../../../../utils/customHooks'
import {
  TooltipContextProvider,
  TooltipProviderContextProvider
} from './tooltip-context'
import { StyledTooltipTrigger } from '../styled-components'

const TooltipTrigger = forwardRef((props, forwardedRef) => {
  const { ...triggerProps } = props

  const context = useContext(TooltipContextProvider)

  const providerContext = useContext(TooltipProviderContextProvider)

  const ref = useRef(null)

  const composedRefs = useComposedRefs(
    forwardedRef,
    ref,
    context.onTriggerChange
  )

  const isPointerDownRef = useRef(false)
  const hasPointerMoveOpenedRef = useRef(false)

  const handlePointerUp = useCallback(
    () => (isPointerDownRef.current = false),
    []
  )

  useEffect(() => {
    return () => document.removeEventListener('pointerup', handlePointerUp)
  }, [handlePointerUp])

  return (
    <StyledTooltipTrigger>
      <div
        // We purposefully avoid adding `type=button` here because tooltip triggers are also
        // commonly anchors and the anchor `type` attribute signifies MIME type.
        aria-describedby={context.open ? context.contentId : undefined}
        data-state={context.stateAttribute}
        {...triggerProps}
        ref={composedRefs}
        onPointerMove={(event) => {
          if (props.onPointerMove) props.onPointerMove()
          if (event.pointerType === 'touch') return
          if (
            !hasPointerMoveOpenedRef.current &&
            !providerContext.isPointerInTransitRef.current
          ) {
            context.onTriggerEnter()
            hasPointerMoveOpenedRef.current = true
          }
        }}
        onPointerLeave={() => {
          if (props.onPointerLeave) props.onPointerLeave()
          context.onTriggerLeave()
          hasPointerMoveOpenedRef.current = false
          context.onClose()
        }}
        onPointerDown={() => {
          if (props.onPointerDown) props.onPointerDown()
          isPointerDownRef.current = true
          document.addEventListener('pointerup', handlePointerUp, {
            once: true
          })
        }}
        onFocus={() => {
          if (props.onFocus) props.onFocus()
          if (!isPointerDownRef.current) context.onOpen()
        }}
        onBlur={() => {
          if (props.onBlur) props.onBlur()
          context.onClose()
        }}
        onClick={() => {
          if (props.onClick) props.onClick()
          context.onClose()
        }}
      />
    </StyledTooltipTrigger>
  )
})

TooltipTrigger.displayName = 'TooltipTrigger'

export default TooltipTrigger
